import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Plus, Trash, Copy, Check, Page } from 'iconoir-react';
import { useNavigate } from 'react-router-dom';

interface ApiToken {
  id: number;
  name: string;
  scopes: string[];
  last_used_at: string | null;
  expires_at: string | null;
  created_at: string;
  is_expired: boolean;
}

interface Scope {
  scope: string;
  description: string;
}

const ApiTokensManagement: React.FC = () => {
  const navigate = useNavigate();
  const [tokens, setTokens] = useState<ApiToken[]>([]);
  const [availableScopes, setAvailableScopes] = useState<Scope[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newToken, setNewToken] = useState<string | null>(null);
  const [copiedToken, setCopiedToken] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    scopes: [] as string[],
    expires_at: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [tokensRes, scopesRes] = await Promise.all([
        api.get('/api-tokens'),
        api.get('/api-tokens/scopes'),
      ]);
      setTokens(tokensRes.data);
      setAvailableScopes(scopesRes.data.scopes);
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateToken = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await api.post('/api-tokens', formData);
      setNewToken(response.data.token);
      setShowCreateForm(false);
      setFormData({ name: '', scopes: [], expires_at: '' });
      await loadData();
    } catch (error) {
      console.error('Erreur lors de la création:', error);
      alert('Erreur lors de la création du token');
    }
  };

  const handleDeleteToken = async (tokenId: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce token ? Cette action est irréversible.')) {
      return;
    }
    try {
      await api.delete(`/api-tokens/${tokenId}`);
      await loadData();
      alert('Token supprimé avec succès');
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression du token');
    }
  };

  const handleRevokeAll = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir révoquer TOUS vos tokens ? Cette action est irréversible.')) {
      return;
    }
    try {
      await api.post('/api-tokens/revoke-all');
      await loadData();
      alert('Tous les tokens ont été révoqués');
    } catch (error) {
      console.error('Erreur lors de la révocation:', error);
      alert('Erreur lors de la révocation des tokens');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedToken(true);
    setTimeout(() => setCopiedToken(false), 2000);
  };

  const toggleScope = (scope: string) => {
    setFormData(prev => ({
      ...prev,
      scopes: prev.scopes.includes(scope)
        ? prev.scopes.filter(s => s !== scope)
        : [...prev.scopes, scope],
    }));
  };

  const getScopeCategory = (scope: string) => {
    return scope.split(':')[0];
  };

  const groupedScopes = availableScopes.reduce((acc, scope) => {
    const category = getScopeCategory(scope.scope);
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(scope);
    return acc;
  }, {} as Record<string, Scope[]>);

  if (loading) return <div>Chargement...</div>;

  return (
    <div className="api-tokens-management">
      <div className="tokens-header">
        <div>
          <h2>Tokens API</h2>
          <p>Gérez vos tokens pour intégrer Ventilio avec vos outils tiers</p>
        </div>
        <div className="tokens-actions">
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/api-documentation')}
          >
            <Page width={18} height={18} strokeWidth={2} />
            Documentation API
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setShowCreateForm(true)}
          >
            <Plus width={18} height={18} strokeWidth={2} />
            Nouveau token
          </button>
        </div>
      </div>

      {/* Affichage du nouveau token */}
      {newToken && (
        <div className="new-token-alert">
          <h3>⚠️ Token créé avec succès</h3>
          <p>Copiez ce token maintenant, il ne sera plus affiché :</p>
          <div className="token-display">
            <code>{newToken}</code>
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => copyToClipboard(newToken)}
            >
              {copiedToken ? (
                <>
                  <Check width={16} height={16} strokeWidth={2} />
                  Copié !
                </>
              ) : (
                <>
                  <Copy width={16} height={16} strokeWidth={2} />
                  Copier
                </>
              )}
            </button>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => setNewToken(null)}
          >
            J'ai copié le token
          </button>
        </div>
      )}

      {/* Formulaire de création */}
      {showCreateForm && (
        <div className="token-form-modal">
          <div className="token-form-content">
            <h3>Créer un nouveau token API</h3>
            <form onSubmit={handleCreateToken}>
              <div className="form-group">
                <label>Nom du token</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Zapier Integration"
                  className="form-control"
                  required
                />
                <small>Donnez un nom descriptif pour identifier ce token</small>
              </div>

              <div className="form-group">
                <label>Permissions (Scopes)</label>
                <div className="scopes-grid">
                  {Object.entries(groupedScopes).map(([category, scopes]) => (
                    <div key={category} className="scope-category">
                      <h4>{category.charAt(0).toUpperCase() + category.slice(1)}</h4>
                      {scopes.map((scope) => (
                        <label key={scope.scope} className="scope-checkbox">
                          <input
                            type="checkbox"
                            checked={formData.scopes.includes(scope.scope)}
                            onChange={() => toggleScope(scope.scope)}
                          />
                          <span>{scope.description}</span>
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Date d'expiration (optionnel)</label>
                <input
                  type="date"
                  value={formData.expires_at}
                  onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                  className="form-control"
                  min={new Date().toISOString().split('T')[0]}
                />
                <small>Laissez vide pour un token sans expiration</small>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={formData.scopes.length === 0}>
                  Créer le token
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCreateForm(false)}
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Liste des tokens */}
      <div className="tokens-list">
        {tokens.length === 0 ? (
          <div className="empty-state">
            <p>Vous n'avez pas encore créé de token API</p>
            <p>Créez-en un pour commencer à intégrer Ventilio avec vos outils</p>
          </div>
        ) : (
          <>
            <div className="tokens-list-header">
              <h3>{tokens.length} token(s)</h3>
              {tokens.length > 0 && (
                <button className="btn btn-sm btn-danger" onClick={handleRevokeAll}>
                  Révoquer tous les tokens
                </button>
              )}
            </div>
            <table className="tokens-table">
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Permissions</th>
                  <th>Dernière utilisation</th>
                  <th>Expiration</th>
                  <th>Créé le</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tokens.map((token) => (
                  <tr key={token.id} className={token.is_expired ? 'token-expired' : ''}>
                    <td>
                      <strong>{token.name}</strong>
                      {token.is_expired && <span className="badge badge-danger">Expiré</span>}
                    </td>
                    <td>
                      <div className="token-scopes">
                        {token.scopes.slice(0, 3).map((scope) => (
                          <span key={scope} className="scope-badge">
                            {scope}
                          </span>
                        ))}
                        {token.scopes.length > 3 && (
                          <span className="scope-badge">+{token.scopes.length - 3}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      {token.last_used_at
                        ? new Date(token.last_used_at).toLocaleDateString('fr-FR')
                        : 'Jamais'}
                    </td>
                    <td>
                      {token.expires_at
                        ? new Date(token.expires_at).toLocaleDateString('fr-FR')
                        : 'Jamais'}
                    </td>
                    <td>{new Date(token.created_at).toLocaleDateString('fr-FR')}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteToken(token.id)}
                      >
                        <Trash width={16} height={16} strokeWidth={2} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
};

export default ApiTokensManagement;
